import { Session, StockSymbol } from '@benzinga/session';
import { QuoteProtos, ScannerProtos } from './scanner_protos';
import { updateObjects, removeKeysFromObject } from '@benzinga/utils';
import { FullDataField, QuotesV3FieldsManager } from '@benzinga/quotes-v3-fields-manager';

export const delta = (
  prevQuotes: QuoteProtos.IQuote[],
  nextQuotes: QuoteProtos.IQuote[],
  session: Session,
): ScannerProtos.IRowUpdatesV2 => {
  const fields = session.getManager(QuotesV3FieldsManager).getCachedScannerDefs() ?? [];
  const updates: ScannerProtos.IUpdateRowUpdate[] = [];
  const adds: ScannerProtos.IAddRowUpdate[] = [];
  const removes: ScannerProtos.IRemoveRowUpdate[] = [];
  const moves: ScannerProtos.IMoveRowUpdate[] = [];

  const prevIndexs = prevQuotes.reduce<Map<StockSymbol, number>>((acc, quote, index) => {
    acc.set(quote.symbol ?? '', index);
    return acc;
  }, new Map<StockSymbol, number>());

  const nextIndex = nextQuotes.reduce<Map<StockSymbol, number>>((acc, quote, index) => {
    acc.set(quote.symbol ?? '', index);
    return acc;
  }, new Map<StockSymbol, number>());

  prevQuotes.forEach((quote, index) => {
    if (!nextIndex.has(quote.symbol ?? '')) {
      removes.push({ previousIndex: index, rowId: quote.symbol });
    }
  });

  nextQuotes.forEach((quote, index) => {
    const previousIndex = prevIndexs.get(quote.symbol ?? '');
    if (previousIndex === undefined) {
      adds.push({ index, rowId: quote.symbol, updated: quote });
    } else {
      if (index !== previousIndex) {
        moves.push({ index, previousIndex, rowId: quote.symbol, ...diff(prevQuotes[previousIndex], quote, fields) });
      } else {
        updates.push({ index, rowId: quote.symbol, ...diff(prevQuotes[previousIndex], quote, fields) });
      }
    }
  });
  return {
    adds,
    moves,
    removes,
    updates,
  };
};

const diff = (
  prevQuote: QuoteProtos.IQuote,
  nextQuote: QuoteProtos.IQuote,
  fields: FullDataField[] | undefined,
): { updated: Partial<QuoteProtos.IQuote>; removed: Partial<QuoteProtos.IQuote> } => {
  const removed: Partial<QuoteProtos.IQuote> = {};
  const updated: Partial<QuoteProtos.IQuote> = {};

  fields?.forEach(f => {
    if (prevQuote[f.name ?? ''] !== nextQuote[f.name ?? '']) {
      if (nextQuote[f.name ?? ''] === undefined || nextQuote[f.name ?? ''] === null) {
        removed[f.name ?? ''] = null;
      } else {
        updated[f.name ?? ''] = nextQuote[f.name ?? ''];
      }
    }
  });
  return {
    removed,
    updated,
  };
};

export const getNextQuotes = (
  prevQuotes: QuoteProtos.IQuote[],
  changes: ScannerProtos.IRowUpdatesV2,
): QuoteProtos.IQuote[] => {
  const adds = changes.adds ?? [];
  const updates = changes.updates ?? [];
  const moves = changes.moves ?? [];
  const removes = changes.removes ?? [];

  const nextQuotes: QuoteProtos.IQuote[] = Array(prevQuotes.length + (adds.length - removes.length));

  let addIndex = 0;
  let moveIndex = 0;
  let updateIndex = 0;

  for (let i = 0; i < prevQuotes.length + (adds.length - removes.length); i += 1) {
    if (addIndex < adds.length && adds[addIndex].index === i) {
      nextQuotes[i] = adds[addIndex].updated ?? {};
      addIndex += 1;
    } else if (moveIndex < moves.length && moves[moveIndex].index == i) {
      const move = moves[moveIndex];
      nextQuotes[i] = merge(prevQuotes[move.previousIndex ?? 0], move.updated ?? undefined, move.removed ?? undefined);

      moveIndex += 1;
    } else if (updateIndex < updates.length && updates[updateIndex].index == i) {
      const update = updates[updateIndex];
      nextQuotes[i] = merge(prevQuotes[update.index ?? 0], update.updated ?? undefined, update.removed ?? undefined);
      updateIndex += 1;
    } else if (i < prevQuotes.length) {
      nextQuotes[i] = prevQuotes[i];
    } else {
      nextQuotes.slice(i);
      break;
    }
  }
  return nextQuotes;
};

const merge = (
  prevQuote?: QuoteProtos.IQuote,
  updated?: QuoteProtos.IQuote,
  removed?: QuoteProtos.IQuote,
): QuoteProtos.IQuote => {
  if (prevQuote) {
    if (updated && removed) {
      return updateObjects(
        removeKeysFromObject(prevQuote, Object.keys(removed) as (keyof QuoteProtos.IQuote)[]),
        updated,
      );
    } else if (removed) {
      return removeKeysFromObject(prevQuote, Object.keys(removed) as (keyof QuoteProtos.IQuote)[]);
    } else {
      return prevQuote;
    }
  } else {
    return updated ?? {};
  }
};
